
import React, { ErrorInfo, useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { isInRecoveryMode, enterRecoveryMode } from '@/utils/deployment/recoveryUtils';
import { toast as sonnerToast } from 'sonner';
import { trackError, ErrorSeverity } from '@/utils/error/errorTracker';
import ErrorBoundaryClass from './ErrorBoundaryClass';
import { recoverFromRecentErrors, attemptAutoRecovery } from '@/utils/error/recoveryActions';

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Global error boundary for the entire application
 * Catches errors, displays fallbacks, and initiates recovery processes
 */
const GlobalErrorBoundary: React.FC<GlobalErrorBoundaryProps> = ({ children }) => {
  const { toast: uiToast } = useToast();
  const [errorCount, setErrorCount] = useState(0);
  const hasShownRecoveryToast = React.useRef(false);
  const recoveryAttempted = React.useRef(false);
  
  // Check if we're already in recovery mode on mount
  useEffect(() => {
    if (isInRecoveryMode() && !hasShownRecoveryToast.current) {
      uiToast({
        title: "Running in Recovery Mode",
        description: "The app is currently in recovery mode due to previous errors.",
        duration: 5000
      });
      hasShownRecoveryToast.current = true;
      
      // Try to auto-recover from last error for better UX
      // Only try once per component mount
      if (!recoveryAttempted.current) {
        recoveryAttempted.current = true;
        setTimeout(() => {
          recoverFromRecentErrors(24); // Try to recover from errors in the last 24 hours
        }, 2000);
      }
    }
  }, [uiToast]);
  
  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    // Increment error count
    setErrorCount(prevCount => {
      const newCount = prevCount + 1;
      
      // Enter recovery mode if we hit multiple errors
      if (newCount >= 3 && !isInRecoveryMode()) {
        enterRecoveryMode();
        sonnerToast("Recovery Mode Activated", {
          description: "Multiple errors detected. App has entered recovery mode.",
          duration: 5000
        });
        
        // Track this event as critical for admin notification
        trackError(
          new Error(`Recovery mode activated after ${newCount} errors: ${error.message}`),
          'RecoverySystem',
          { lastError: error.message },
          ErrorSeverity.CRITICAL
        );
        
        // Try immediate recovery
        setTimeout(() => attemptAutoRecovery(error), 1000);
      }
      
      return newCount;
    });
    
    // Record error for analytics (using try-catch to prevent cascading errors)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const errorLog = {
          timestamp: new Date().toISOString(),
          message: error.message,
          componentStack: errorInfo.componentStack,
          path: window.location.pathname
        };
        
        localStorage.setItem(`global_error_${Date.now()}`, JSON.stringify(errorLog));
        localStorage.setItem('last_error', JSON.stringify(errorLog));
        
        // Clean up old logs
        const allKeys = Object.keys(localStorage)
          .filter(key => key.startsWith('global_error_'))
          .sort();
          
        if (allKeys.length > 10) {
          allKeys.slice(0, allKeys.length - 10).forEach(key => {
            try {
              localStorage.removeItem(key);
            } catch (e) {
              // Silent fail
            }
          });
        }
      }
    } catch (e) {
      // Silent fail - don't cause additional errors
    }
  };
  
  const showToast = (message: string) => {
    uiToast({
      title: "Application Error",
      description: message,
      variant: "destructive",
      duration: 5000
    });
  };
  
  return (
    <ErrorBoundaryClass 
      showToast={showToast} 
      onError={handleError}
    >
      {children}
    </ErrorBoundaryClass>
  );
};

export default GlobalErrorBoundary;
